import React from 'react';
import { BetaStatusContent, Tip, ReportingMethod, Stat } from './betaTypes';

const inputStyle = {
  backgroundColor: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.15)',
  color: 'var(--rp-text)',
};

export interface BetaListEditorsProps {
  content: BetaStatusContent;
  editingTip: number | null;
  editingReport: number | null;
  editingStat: number | null;
  setEditingTip: (i: number | null) => void;
  setEditingReport: (i: number | null) => void;
  setEditingStat: (i: number | null) => void;
  addTip: () => void;
  updateTip: (index: number, field: keyof Tip, value: string) => void;
  removeTip: (index: number) => void;
  addReporting: () => void;
  updateReporting: (index: number, updates: Partial<ReportingMethod>) => void;
  removeReporting: (index: number) => void;
  addStat: () => void;
  updateStat: (index: number, updates: Partial<Stat>) => void;
  removeStat: (index: number) => void;
}

const BetaListEditors: React.FC<BetaListEditorsProps> = (props) => {
  const {
    content, editingTip, editingReport, editingStat, setEditingTip, setEditingReport,
    setEditingStat, addTip, updateTip, removeTip, addReporting, updateReporting,
    removeReporting, addStat, updateStat, removeStat,
  } = props;
  return (
    <>
      {/* Testing Tips */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--rp-text)' }}>Testing Tips ({content.tips.length})</h3>
          <button onClick={addTip} className="px-3 py-1 rounded-lg text-xs font-semibold transition-all hover:bg-white/10"
            style={{ color: '#3b82f6', border: '1px solid rgba(59,130,246,0.3)' }}>
            + Add Tip
          </button>
        </div>
        <div className="space-y-2">
          {content.tips.map((tip, i) => (
            <div key={i} className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              {editingTip === i ? (
                <div className="space-y-2">
                  <input type="text" value={tip.title} placeholder="Tip title" aria-label={`Tip ${i + 1} title`}
                    onChange={e => updateTip(i, 'title', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}
                  />
                  <textarea value={tip.description} placeholder="Tip description" aria-label={`Tip ${i + 1} description`}
                    onChange={e => updateTip(i, 'description', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm resize-none" rows={2} style={inputStyle}
                  />
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => removeTip(i)} className="admin-btn admin-btn-danger text-xs">Remove</button>
                    <button onClick={() => setEditingTip(null)} className="admin-btn admin-btn-primary text-xs">Done</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between cursor-pointer" onClick={() => setEditingTip(i)}>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white"
                      style={{ backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444'][i % 5] }}>
                      {i + 1}
                    </div>
                    <div>
                      <span className="text-sm font-semibold" style={{ color: 'var(--rp-text)' }}>{tip.title || '(untitled)'}</span>
                      <p className="text-xs line-clamp-1" style={{ color: 'var(--rp-text-tertiary)' }}>{tip.description}</p>
                    </div>
                  </div>
                  <span className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>Click to edit</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Reporting Methods */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--rp-text)' }}>Reporting Methods ({content.reporting.length})</h3>
          <button onClick={addReporting} className="px-3 py-1 rounded-lg text-xs font-semibold transition-all hover:bg-white/10"
            style={{ color: '#8b5cf6', border: '1px solid rgba(139,92,246,0.3)' }}>
            + Add Method
          </button>
        </div>
        <div className="space-y-2">
          {content.reporting.map((r, i) => (
            <div key={i} className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              {editingReport === i ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-[60px_1fr] gap-2">
                    <input type="text" value={r.icon} placeholder="Icon" aria-label={`Reporting method ${i + 1} icon`}
                      onChange={e => updateReporting(i, { icon: e.target.value })}
                      className="px-2 py-2 rounded-lg text-sm text-center" style={inputStyle}
                    />
                    <input type="text" value={r.title} placeholder="Method title" aria-label={`Reporting method ${i + 1} title`}
                      onChange={e => updateReporting(i, { title: e.target.value })}
                      className="px-3 py-2 rounded-lg text-sm" style={inputStyle}
                    />
                  </div>
                  <textarea value={r.description || ''} placeholder="Description (optional)" aria-label={`Reporting method ${i + 1} description`}
                    onChange={e => updateReporting(i, { description: e.target.value || undefined })}
                    className="w-full px-3 py-2 rounded-lg text-sm resize-none" rows={2} style={inputStyle}
                  />
                  <div>
                    <label className="text-xs mb-1 block" style={{ color: 'var(--rp-text-secondary)' }} htmlFor={`beta-report-items-${i}`}>Bullet points (one per line)</label>
                    <textarea id={`beta-report-items-${i}`} value={(r.items || []).join('\n')} placeholder="One bullet per line"
                      onChange={e => updateReporting(i, { items: e.target.value ? e.target.value.split('\n') : [] })}
                      className="w-full px-3 py-2 rounded-lg text-sm resize-none" rows={3} style={inputStyle}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" value={r.link_text || ''} placeholder="Button text (optional)" aria-label={`Reporting method ${i + 1} button text`}
                      onChange={e => updateReporting(i, { link_text: e.target.value || undefined })}
                      className="px-3 py-2 rounded-lg text-sm" style={inputStyle}
                    />
                    <input type="text" value={r.link_url || ''} placeholder="Button URL (optional)" aria-label={`Reporting method ${i + 1} button URL`}
                      onChange={e => updateReporting(i, { link_url: e.target.value || undefined })}
                      className="px-3 py-2 rounded-lg text-sm" style={inputStyle}
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => removeReporting(i)} className="admin-btn admin-btn-danger text-xs">Remove</button>
                    <button onClick={() => setEditingReport(null)} className="admin-btn admin-btn-primary text-xs">Done</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between cursor-pointer" onClick={() => setEditingReport(i)}>
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{r.icon}</span>
                    <span className="text-sm font-semibold" style={{ color: 'var(--rp-text)' }}>{r.title || '(untitled)'}</span>
                  </div>
                  <span className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>Click to edit</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Stats */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--rp-text)' }}>Bottom Stats ({content.stats.length})</h3>
          <button onClick={addStat} className="px-3 py-1 rounded-lg text-xs font-semibold transition-all hover:bg-white/10"
            style={{ color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}>
            + Add Stat
          </button>
        </div>
        <div className="space-y-2">
          {content.stats.map((stat, i) => (
            <div key={i} className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              {editingStat === i ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label htmlFor={`beta-stat-label-${i}`} className="text-xs mb-1 block" style={{ color: 'var(--rp-text-secondary)' }}>Label</label>
                      <input id={`beta-stat-label-${i}`} type="text" value={stat.label} placeholder="Stat label"
                        onChange={e => updateStat(i, { label: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}
                      />
                    </div>
                    <div>
                      <label htmlFor={`beta-stat-value-${i}`} className="text-xs mb-1 block" style={{ color: 'var(--rp-text-secondary)' }}>Value</label>
                      <input id={`beta-stat-value-${i}`} type="text" value={stat.value_type === 'auto_build' ? '(auto: latest build)' : (stat.value || '')}
                        disabled={stat.value_type === 'auto_build'}
                        placeholder="Display value"
                        onChange={e => updateStat(i, { value: e.target.value, value_type: undefined })}
                        className="w-full px-3 py-2 rounded-lg text-sm disabled:opacity-50" style={inputStyle}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={stat.value_type === 'auto_build'}
                        onChange={e => updateStat(i, e.target.checked ? { value_type: 'auto_build', value: undefined } : { value_type: undefined, value: '' })}
                        className="rounded"
                      />
                      <span className="text-xs" style={{ color: 'var(--rp-text-secondary)' }}>Auto-fill from latest changelog build</span>
                    </label>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => removeStat(i)} className="admin-btn admin-btn-danger text-xs">Remove</button>
                    <button onClick={() => setEditingStat(null)} className="admin-btn admin-btn-primary text-xs">Done</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between cursor-pointer" onClick={() => setEditingStat(i)}>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold" style={{ color: 'var(--rp-text)' }}>
                      {stat.value_type === 'auto_build' ? '(auto)' : stat.value} — {stat.label || '(no label)'}
                    </span>
                  </div>
                  <span className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>Click to edit</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default BetaListEditors;
